
import { Component, Vue } from "vue-property-decorator";
import vBreadcrumbs from "@/components/vBreadcrumbs.vue";
import vTable from "@/components/vTable.vue";
import vModal from "@/components/vModal.vue";
import { mapState } from "vuex";
import { TRANSLATIONS_FETCH } from "@/store/ActionTypes";
import ListRequest, { Ordering, Filter, OrderType } from "@/models/ListRequest";
import { getErrorMessage } from "@/utils/Helpers";
import { IngredientCost } from "@/models/IngredientCost/IngredientCost";
import { MeasurementCategory } from "@/models/Measurement/MeasurementCategory";
import IngredientCostRepository from "@/utils/API/IngredientCostRepository";
import { dateTimeDisplayFormat } from "@/utils/Defaults";
import MeasurementRepository from "../../utils/API/MeasurementRepository";

const vSelectFx = () => import("@/components/vSelectFx.vue");

@Component({
  components: {
    vBreadcrumbs,
    vModal,
    vSelectFx,
    vTable,
  },
  computed: {
    ...mapState({
      localizationState: (state: any) => state.LocalizationModule,
    }),
  },
})
class IngredientCostComponent extends Vue {
  localizationState: any;

  ingredientCosts: IngredientCost[] = [];
  newIngredientCosts: any[] = [];
  massMeasurements: any[] = [];
  vendors: any[] = [];

  totalCount: number = 0;
  pageSize: number = 10;
  count: number = 0;
  page: number = 1;
  filter: Filter = {
    name: "",
    value: "",
  };
  ordering: Ordering = {
    field: "",
    orderType: OrderType.Asc,
  };

  editingIngredientCost: any = {};
  addingVendorName: string = "";
  loading: boolean = false;
  searchString = "";

  addingModal = {
    isOpen: false,
  };

  addingVendorModal = {
    isOpen: false,
  };

  editingModal = {
    isOpen: false,
  };

  deletingModal = {
    isOpen: false,
    isDeletingIngrCostUsed: false,
    id: 0,
  };

  importingModal = {
    isOpen: false,
    vendorId: this.vendors.length > 0 ? this.vendors[0].id : null,
    file: "",
  };

  addingMeasurementModal = {
    ingredientCostId: 0,
    isOpen: false,
    newMeasurements: new Array(),
    ingrCostIndex: -1,
  };

  vendorEmpty: boolean = false;

  // Store import ingredient cost file
  storeFile() {
    // @ts-ignore
    this.importingModal.file = this.$refs.file.files[0];
  }
  // Close import ingredient cost modal
  importingModalClose() {
    this.importingModal.file = null;
    this.importingModal.isOpen = false;
    this.vendorEmpty = false;
  }
  // Remove ingredient cost row
  removeIngrCostRow(index: any) {
    this.newIngredientCosts.splice(index, 1);
  }

  // Remove measurement row
  removeMeasurementRow(index: any) {
    this.addingMeasurementModal.newMeasurements.splice(index, 1);
    setTimeout(() => {
      this.$validator.validateAll("measurement");
    }, 300);
  }

  // Open Import Ingredient cost
  openImportIngredientCostModal() {
    this.importingModal.isOpen = true;
    this.importingModal.vendorId =
      this.vendors.length > 0 ? this.vendors[0].id : null;
  }

  // Open Add measurement modal
  openAddMeasurementModal(index: number, ingrCost: any) {
    this.addingMeasurementModal.newMeasurements.length = 0;
    this.addingMeasurementModal.ingrCostIndex = index;
    this.addingMeasurementModal.isOpen = true;
    // @ts-ignore
    this.$snotify.async(
      this.translations.get("ingr-cost-loading"),
      () =>
        new Promise((success, reject) => {
          IngredientCostRepository.loadMeasurementsForIngredientCost({
            id: null,
            name: ingrCost.name,
            measurements: ingrCost.ingredientCostMeasurements,
          })
            .then((response) => {
              if (response.data.result && response.data.result.length > 0) {
                this.addingMeasurementModal.newMeasurements.shift();
              }
              // @ts-ignore
              response.data.result.map((x) => {
                this.addingMeasurementModal.newMeasurements.push({
                  // @ts-ignore
                  name: x.name,
                  // @ts-ignore
                  weight: (Math.round(x.weightInGrams * 100) / 100).toFixed(2),
                });
              });
              success({
                body: this.translations.get("ingr-cost-save-success"),
              });
            })
            .catch((error) => {
              reject({
                body: getErrorMessage(error, this.translations),
              });
            });
        }),
      {
        timeout: 1000,
        closeOnClick: true,
      }
    );
    // @ts-ignore
    this.addingMeasurementModal.newMeasurements.push(this.getEmptyMeasurment());
  }

  // Open Edit measuremenet modal
  openEditMeasurementModal() {
    this.addingMeasurementModal.newMeasurements.length = 0;
    this.addingMeasurementModal.ingrCostIndex = -1;
    this.addingMeasurementModal.isOpen = true;
    // @ts-ignore
    this.$snotify.async(
      this.translations.get("ingr-cost-loading"),
      () =>
        new Promise((success, reject) => {
          IngredientCostRepository.loadMeasurementsForIngredientCost({
            id: this.editingIngredientCost.id,
            name: this.editingIngredientCost.name,
            // @ts-ignore
            measurements: this.editingIngredientCost.ingredientCostMeasurements,
          })
            .then((response) => {
              if (response.data.result && response.data.result.length > 0) {
                this.addingMeasurementModal.newMeasurements.shift();
              }

              // @ts-ignore
              response.data.result.map((x) => {
                this.addingMeasurementModal.newMeasurements.push({
                  // @ts-ignore
                  name: x.name,
                  // @ts-ignore
                  weight: (Math.round(x.weightInGrams * 100) / 100).toFixed(2),
                });
              });
              success({
                body: this.translations.get("ingr-cost-save-success"),
              });
            })
            .catch((error) => {
              reject({
                body: getErrorMessage(error, this.translations),
              });
            });
        }),
      {
        timeout: 1000,
        closeOnClick: true,
      }
    );
    // @ts-ignore
    this.addingMeasurementModal.newMeasurements.push(this.getEmptyMeasurment());
  }

  // Edit Ingredient cost
  editIngredientCost(id: number) {
    let ingrCost = this.ingredientCosts.find((x) => id === x.id);
    if (ingrCost != undefined) {
      this.editingIngredientCost = {
        id: ingrCost.id,
        name: ingrCost.name,
        vendorId: ingrCost.vendorId,
        quantity: ingrCost.quantity,
        chosenMeasurementName:
          ingrCost.ingredientCostMeasurement.measurement.name,
        ingredientCostMeasurements: ingrCost.ingredientCostMeasurements.map(
          (x) => {
            return {
              id: x.id,
              name: x.measurement.name,
              ingredientCostId: x.ingredientCostId,
              measurementId: x.measurementId,
              weightInGrams: x.weightInGrams,
            };
          }
        ),
        cost: ingrCost.cost,
      };
    }

    this.editingModal.isOpen = true;
  }

  // Save the editing ingredient cost
  submitEditIngredientCost() {
    this.$validator.validateAll("editIngredientCost").then((result) => {
      if (result) {
        // @ts-ignore
        this.$snotify.async(
          this.translations.get("ingr-cost-loading"),
          () =>
            new Promise((success, reject) => {
              IngredientCostRepository.updateIngredientCost(
                this.editingIngredientCost
              )
                .then(() => {
                  this.editingModal.isOpen = false;
                  this.requestIngredientCost();
                  success({
                    body: this.translations.get("ingr-cost-edit-success"),
                  });
                })
                .catch((error) => {
                  reject({
                    body: getErrorMessage(error, this.translations),
                  });
                });
            }),
          {
            timeout: 3500,
            closeOnClick: true,
          }
        );
      }
    });
  }

  // Check if ingredient cost name already exists in ingredinet cost list
  checkDuplicateIngrCostNames() {
    for (let i = 0; i < this.newIngredientCosts.length; i++) {
      for (let j = i + 1; j < this.newIngredientCosts.length; j++) {
        if (
          this.newIngredientCosts[i].name == this.newIngredientCosts[j].name &&
          this.newIngredientCosts[i].vendorId != null &&
          this.newIngredientCosts[j].vendorId != null &&
          this.newIngredientCosts[i].vendorId ==
          this.newIngredientCosts[j].vendorId
        ) {
          return true;
        }
        return false;
      }
    }
    return false;
  }

  // Save ingredient cost button modal
  saveIngredientCost() {
    if (this.checkDuplicateIngrCostNames()) {
      this.$snotify.error(this.translations.get("ingr-cost-error-name-exist"));
      return;
    }
    this.$validator.validateAll("ingredientCost").then((result) => {
      if (result) {
        // @ts-ignore
        this.$snotify.async(
          this.translations.get("ingr-cost-loading"),
          () =>
            new Promise((success, reject) => {
              IngredientCostRepository.createIngredientCosts(
                this.newIngredientCosts
              )
                .then(() => {
                  this.addingModal.isOpen = false;
                  this.requestIngredientCost();
                  success({
                    body: this.translations.get("ingr-cost-save-success"),
                  });
                })
                .catch((error) => {
                  this.loading = false;
                  reject({
                    body: getErrorMessage(error, this.translations),
                  });
                });
            }),
          { closeOnClick: true, timeout: 3500 }
        );
      }
    });
  }

  // Add new row in new ingredien costs modal
  addNewIngrCostRow() {
    this.newIngredientCosts.push(this.getEmptyIngredientCost());
  }

  // Empty measurement json
  getEmptyMeasurment() {
    return {
      name: "",
      weight: 0,
    };
  }

  // Add new row in new ingredien costs modal
  addNewMeasurementRow() {
    // @ts-ignore
    this.addingMeasurementModal.newMeasurements.push(this.getEmptyMeasurment());
  }

  submitNewMeasurements() {
    if (this.addingMeasurementModal.ingrCostIndex > -1) {
      this.addNewMeasurementsForNewIngredientCost();
    }
    else {
      this.addNewMeasurementsForExistingIngredientCost();
    }
  }

  // Add new measurement for new ingredient cost
  addNewMeasurementsForNewIngredientCost() {
    this.$validator.validateAll("measurement").then((result) => {
      if (result) {
        // @ts-ignore
        let ingrCost =
          this.newIngredientCosts[this.addingMeasurementModal.ingrCostIndex];
        //  @ts-ignore
        let measurementNames = ingrCost.ingredientCostMeasurements.map(
          //  @ts-ignore
          (x) => x.name
        );
        this.addingMeasurementModal.newMeasurements.map((x) => {
          //  @ts-ignore
          if (!measurementNames.includes(x.name)) {
            ingrCost.ingredientCostMeasurements.push({
              //  @ts-ignore
              name: x.name,
              //  @ts-ignore
              weightInGrams: x.weight,
            });
          }
          return x;
        });
        this.addingMeasurementModal.isOpen = false;
      }
    });
  }

  // Add new measurements for new existing ingredient cost
  addNewMeasurementsForExistingIngredientCost() {
    this.$validator.validateAll("measurement").then((result) => {
      if (result) {
        //  @ts-ignore
        let measurementNames =
          this.editingIngredientCost.ingredientCostMeasurements.map(
            //  @ts-ignore
            (x) => x.name
          );
        this.addingMeasurementModal.newMeasurements.map((x) => {
          //  @ts-ignore
          if (!measurementNames.includes(x.name)) {
            this.editingIngredientCost.ingredientCostMeasurements.push({
              //  @ts-ignore
              name: x.name,
              //  @ts-ignore
              weightInGrams: x.weight,
            });
          }
          return x;
        });
        this.addingMeasurementModal.isOpen = false;
      }
    });
  }

  // Add new vendor
  saveIngredientCostVendor() {
    this.$validator.validateAll("vendor").then((result) => {
      if (result) {
        // @ts-ignore
        this.$snotify.async(
          this.translations.get("ingr-cost-loading"),
          () =>
            new Promise((success, reject) => {
              IngredientCostRepository.createIngredientCostVendor(
                this.addingVendorName
              )
                .then(() => {
                  this.addingVendorModal.isOpen = false;
                  this.loadIngredientCostVendors();
                  this.addingVendorName = "";
                  success({
                    body: this.translations.get("ingr-cost-vendor-success"),
                  });
                })
                .catch((error) => {
                  reject({
                    body: getErrorMessage(error, this.translations),
                  });
                });
            }),
          {
            timeout: 3500,
            closeOnClick: true,
          }
        );
      }
    });
  }

  // Delete ingredient cost
  deleteIngredientCost(id: number) {
    let ingrCost = this.ingredientCosts.find((x) => x.id === id);
    if (ingrCost != undefined) {
      this.deletingModal.isOpen = true;
      this.deletingModal.isDeletingIngrCostUsed = ingrCost.isUsed;
      this.deletingModal.id = ingrCost.id;
    }
  }

  // Submit delete ingredient cost
  submitDelete() {
    // @ts-ignore
    this.$snotify.async(
      this.translations.get("ingr-cost-loading"),
      () =>
        new Promise((success, reject) => {
          IngredientCostRepository.deleteIngredientCost(this.deletingModal.id)
            .then(() => {
              this.requestIngredientCost();
              this.deletingModal.isOpen = false;
              this.page = 1;
              success({
                body: this.translations.get("ingr-cost-delete-success"),
              });
            })
            .catch((error) => {
              reject({
                body: getErrorMessage(error, this.translations),
              });
            });
        }),
      {
        timeout: 3500,
        closeOnClick: true,
      }
    );
  }

  // Import ingredient cost - submit
  importSubmit() {
    let formData = new FormData();
    formData.append("file", <any>this.importingModal.file);

    if (this.importingModal.vendorId == null) {
      this.vendorEmpty = true;
    }
    else {
      // @ts-ignore
      this.$snotify.async(
        this.translations.get("ingr-cost-loading"),
        () =>
          new Promise((success, reject) => {
            IngredientCostRepository.uploadIngredientCosts(formData)
              .then((response) => {
                this.newIngredientCosts.length = 0;
                // @ts-ignore
                response.data.result.map((x) => {
                  let ingr = this.getEmptyIngredientCost();
                  ingr.name = x.name;
                  ingr.cost = x.cost;
                  ingr.quantity = x.quantity;
                  ingr.vendorId = this.importingModal.vendorId;
                  ingr.chosenMeasurementName = x.measurement;

                  this.newIngredientCosts.push(ingr);
                  return x;
                });
                this.importingModal.isOpen = false;
                this.addingModal.isOpen = true;
                this.importingModal.file = null;
                this.vendorEmpty = false;
                success({
                  body: this.translations.get("ingr-cost-save-success"),
                });
              })
              .catch((error) => {
                reject({
                  body: getErrorMessage(error, this.translations),
                });
              });
          }),
        {
          timeout: 3500,
          closeOnClick: true,
        }
      );
    }
  }

  resetNewIngredientCosts() {
    this.newIngredientCosts = [this.getEmptyIngredientCost()];
  }

  changePage(args: any) {
    let page = args[0];
    let filters = args[2];
    let orderings = args[3];

    this.page = page;
    this.ordering =
      orderings.length > 0
        ? orderings[0]
        : { field: "", orderType: OrderType.Asc };
    this.filter = filters.length > 0 ? filters[0] : { name: "", value: "" };

    this.requestIngredientCost();
  }

  order(filters: Filter[], orders: Ordering) {
    this.ordering = orders;
    this.filter = filters.length > 0 ? filters[0] : { name: "", value: "" };

    this.requestIngredientCost();
  }

  // Request for ListIngredientCost
  requestIngredientCost() {
    let orderings = [this.ordering];
    let filters = [this.filter];

    let reqData = new ListRequest({
      page: this.page,
      pageSize: this.pageSize,
      filters: filters,
      orders: orderings,
    });

    this.loading = true;

    IngredientCostRepository.listIngredientCost(reqData)
      .then((response) => {
        this.count = response.data.count;
        this.totalCount = response.data.totalCount;
        this.ingredientCosts = response.data.result;
        this.loading = false;
      })
      .catch((error) => {
        this.loading = false;
        this.$snotify.error(getErrorMessage(error, this.translations));
      });
  }

  // Open Add ingredient cost modal
  openAddIngredientCostModal() {
    this.resetNewIngredientCosts();
    this.addingModal.isOpen = true;
  }

  openAddVendorModal() {
    this.addingVendorModal.isOpen = true;
  }

  // Empty ingredient cost json
  getEmptyIngredientCost() {
    return {
      name: "",
      vendorId: this.vendors.length > 0 ? this.vendors[0].id : null,
      quantity: 0,
      chosenMeasurementName:
        this.massMeasurements.length > 0 ? this.massMeasurements[0].name : null,
      ingredientCostMeasurements: [...this.massMeasurements],
      cost: 0,
    };
  }

  // Load list of ingredient cost Vendors
  loadIngredientCostVendors() {
    IngredientCostRepository.listIngredientCostVendors()
      .then((response) => {
        let vendors = response.data.result.map((x: any) => {
          return {
            id: x.id,
            name: x.name,
          };
        });
        this.vendors = vendors;
      })
      .catch((error) => {
        this.$snotify.error(getErrorMessage(error, this.translations));
      });
  }

  get ingredientCostsForGrid() {
    return this.ingredientCosts.map((x) => {
      return {
        meta: {
          id: x.id,
          link: x.name,
        },
        e1: x.vendor.name,
        e2: x.quantity,
        e3: x.ingredientCostMeasurement.measurement.name,
        e4: x.cost.toFixed(2),
        e5: this.$moment(x.createdOn).format(dateTimeDisplayFormat),
        e6: this.$moment(x.updatedOn).format(dateTimeDisplayFormat),
      };
    });
  }

  // Download Import Button
  get downloadImport() {
    return `${process.env.VUE_APP_BASE_DOMAIN}/api/Processes/Ingredientcosts/DownloadIngredientCostImportingTemplate`;
  }

  // Export Button
  get exportFile() {
    return `${process.env.VUE_APP_BASE_DOMAIN}/api/Processes/Ingredientcosts/ExportIngredientCosts`;
  }

  get tableHeaders() {
    return [
      {
        name: this.translations.get("ingr-cost-table-name"),
        key: "Name",
        searchable: true,
        searchString: "",
        sortable: true,
      },
      {
        name: this.translations.get("ingr-cost-table-vendor"),
        key: "Vendor",
        searchable: false,
        searchString: "",
        sortable: true,
      },
      {
        name: this.translations.get("ingr-cost-table-quantity"),
        key: "Quantity",
        searchable: false,
        searchString: "",
        sortable: true,
      },
      {
        name: this.translations.get("ingr-cost-table-measurement"),
        key: "Measurement",
        searchable: false,
        searchString: "",
        sortable: true,
      },
      {
        name: this.translations.get("ingr-cost-table-cost"),
        key: "Cost",
        searchable: false,
        searchString: "",
        sortable: true,
      },
      {
        name: this.translations.get("ingr-cost-table-date"),
        key: "CreatedOn",
        searchable: false,
        searchString: "",
        sortable: true,
      },
      {
        name: this.translations.get("ingr-cost-table-updated"),
        key: "UpdatedOn",
        searchable: false,
        searchString: "",
        sortable: true,
      },
    ];
  }

  get hasIngredientCosts() {
    return this.ingredientCosts.length > 0;
  }

  get translations() {
    return this.localizationState.translations;
  }

  created() {
    this.$store
      .dispatch(TRANSLATIONS_FETCH, {
        category: "pages",
        collections: ["common", "ingredients", "ingredient cost"],
      })
      .then(() => {
        document.title =
          this.translations.get("ingr-cost-title") +
          " " +
          this.translations.get("meta_title_postfix");
      })
      .catch((error) => {
        this.$snotify.error(getErrorMessage(error, this.translations));
      });

    this.requestIngredientCost();

    this.loadIngredientCostVendors();

    MeasurementRepository.listMeasurementsForCategory(MeasurementCategory.Mass)
      .then((response) => {
        let massMeasurements = response.data.result.map((x: any) => {
          return {
            id: x.id,
            name: x.name,
            weightInGrams: x.conversionCoeficient,
          };
        });

        MeasurementRepository.listMeasurementsForCategory(
          MeasurementCategory.Volume
        )
          .then((volResponse) => {
            let volumeMeasurements = volResponse.data.result
              .map((x: any) => {
                return {
                  id: x.id,
                  name: x.name,
                  weightInGrams: x.conversionCoeficient,
                };
              })
              .filter((x: any) => {
                return ["ml", "l", "fl. oz"].includes(x.name.toLowerCase());
              });

            this.massMeasurements = massMeasurements.concat(volumeMeasurements);
          })
          .catch((error) => {
            this.$snotify.error(getErrorMessage(error, this.translations));
          });
      })
      .catch((error) => {
        this.$snotify.error(getErrorMessage(error, this.translations));
      });
  }

  mounted() {
    (<any>window).$.Pages.init();

    (<any>$("#select2IngredientCost"))
      .select2({
        allowClear: false,
        width: "100%",
        ajax: {
          delay: 500,
          transport: (params: any, success: any) => {
            IngredientCostRepository.listIngredientCost(
              new ListRequest({
                page: 1,
                pageSize: this.pageSize,
                filters: [
                  {
                    name: "Name",
                    value: params.data.term ? params.data.term : "",
                  },
                ],
                orders: [
                  {
                    field: "Name",
                    orderType: OrderType.Asc,
                  },
                ],
              })
            )
              .then((response) => {
                success(response);
              })
              .catch((error) => {
                this.$snotify.error(getErrorMessage(error, this.translations));
              });
          },
          processResults: (response: any) => {
            let data: any[] = [];
            data.push({ id: 0, text: "All Ingredient costs" });
            response.data.result.map((r: any) => {
              data.push({ id: r.id, text: r.name });
            });

            return { results: data };
          },
        },
      })
      .on("select2:select", (eventData: any) => {
        this.page = 1;
        this.ordering = { field: "", orderType: OrderType.Asc };

        if (eventData.target.value == 0) {
          this.filter = {
            name: "",
            value: "",
          };
        }
        else {
          this.filter = { name: "Id", value: eventData.target.value };
        }

        this.requestIngredientCost();
      });
  }
}
export default IngredientCostComponent;
